import { useQuery, useReactiveVar } from "@apollo/client";
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
} from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { ComponentLoading } from "../../components/loading/ComponentLoading";
import { GET_PRECISION_CATEGORIES_OPTIONS } from "../../graphql/operations/campaignOperations/operations";
import { userDetailsVar } from "../../lib/cache";
import { Header, ReviewTypeEnum } from "./Header";
import { HITLBodyContent } from "./HITLBodyContent";
import { AccountsAgencyPartnerChoices, PrecisionCategoriesOptionsType } from "../../graphql/generated";
import { PublisherEnum, RangeEnum } from "../../graphqlExternal/generated";
import { formatTextDisplay, getDateRangeLabel } from "../../utils/displayGetters";
import { publisherMap } from "../../utils/publisherMap";
import { TitleLink } from "./TitleLink";
import { decodeURL } from "../../utils/encodeUrl";

export enum ClassificationCategories {
  precision = "precision_iab_v3",
  brandSafety = "cat_brandsafety",
  entities = "illuma_ents",
  sentiment = "illuma_sent",
  emotion = "illuma_emotion",
}

export enum EntityCategories {
  date = "DATE",
  event = "EVENT",
  gpe = "GPE",
  language = "LANGUAGE",
  loc = "LOC",
  // money = "MONEY",
  norp = "NORP",
  org = "ORG",
  person = "PERSON",
  product = "PRODUCT",
  workOfArt = "WORK_OF_ART",
}

export interface IHITLFilters {
  urlSearch: string;
  categoryFilter: { label: string; value: string };
  correctionsFilter: { label: string; value: ReviewTypeEnum };
  publisherFilter: {label: string, value: PublisherEnum};
  dateRangeFilter: {label: string, value: RangeEnum};
  // selectedUrl: string;
  // setSelectedUrl: Dispatch<SetStateAction<string>>;
  urlIndex: number;
  setUrlIndex: Dispatch<SetStateAction<number>>;
  urlList: string[];
  setUrlList: Dispatch<SetStateAction<string[]>>;
  cursor: number;
  setCursor: Dispatch<SetStateAction<number>>;
  // selectedIndex: number;
  // setSelectedIndex: Dispatch<SetStateAction<number>>;
  urlLimit: number;
  setUrlLimit: Dispatch<SetStateAction<number>>;
  totalListCount: number;
  setTotalListCount: Dispatch<SetStateAction<number>>;
  endOfPages: boolean;
  setEndOfPages: Dispatch<SetStateAction<boolean>>;
  startDateFilter: string|null;
  setStartDateFilter: Dispatch<SetStateAction<string|null>>;
  endDateFilter: string|null;
  setEndDateFilter: Dispatch<SetStateAction<string|null>>;

}

const hitlFiltersContext = createContext({} as IHITLFilters);

export const useHitlFilters = () => {
  return useContext(hitlFiltersContext);
};

export const HumanInTheLoop = () => {
  const location = useLocation();
  const shouldDisplayHeader = location.pathname.includes('/url-list');
  const isReviewPage = location.pathname.includes('/review');
  const isPredictionsPage = location.pathname.includes('/predictive-correction')
  const isCorrectionsPage = location.pathname.includes('/past-review')
  const url = decodeURL(useParams().url || "");

  const currentUser = useReactiveVar(userDetailsVar);
  const [urlSearch, setUrlSearch] = useState("");
  // const [selectedUrl, setSelectedUrl] = useState("");
  // const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [urlList, setUrlList] = useState<string[]>([]);
  const [cursor, setCursor] = useState<number>(0);
  const [urlLimit, setUrlLimit] = useState(15);
  const [totalListCount, setTotalListCount] = useState<number>(0);
  const [endOfPages, setEndOfPages] = useState<boolean>(false)
  const [urlIndex, setUrlIndex] = useState<number>(1);
  const [categoryFilter, setCategoryFilter] = useState({
    label: "All Categories",
    value: "ALL",
  });
  const [correctionsFilter, setCorrectionsFilter] = useState<{label: string, value: ReviewTypeEnum}>({
    label: "All",
    value: ReviewTypeEnum.All,
  });
  const [publisherFilter, setPublisherFilter] = useState<{label: string, value: PublisherEnum}>(
    assignUserPublisher(currentUser?.agency.partner)
  );
  const [dateRangeFilter, setDateRangeFilter] = useState<{label: string, value: RangeEnum}>(
    {label: getDateRangeLabel(RangeEnum.Last_7Days), value: RangeEnum.Last_7Days}
  );

  const [startDateFilter, setStartDateFilter] = useState<string|null>(null);
  const [endDateFilter, setEndDateFilter] = useState<string|null>(null);

  const {
    data: precisionData,
    error: precisionError,
    loading: precisionLoading,
  } = useQuery(GET_PRECISION_CATEGORIES_OPTIONS, {
    fetchPolicy: "cache-and-network",
  });

  if (precisionLoading || !currentUser) {
    return <ComponentLoading text={"Loading page..."} />;
  }

  if (!currentUser?.isStaff) {
    if (!currentUser?.agency.partner) {
      return <Navigate to={"/campaigns"} />;
    }
  }

  if (precisionError) {
    return <>Error getting URL and categories</>;
  }



  return (
    <hitlFiltersContext.Provider
      value={{
        urlSearch: urlSearch,
        categoryFilter: categoryFilter,
        publisherFilter: publisherFilter,
        dateRangeFilter: dateRangeFilter,
        startDateFilter: startDateFilter,
        setStartDateFilter: setStartDateFilter,
        setEndDateFilter: setEndDateFilter,
        endDateFilter: endDateFilter,

        // selectedUrl: selectedUrl,
        // setSelectedUrl: setSelectedUrl,
        urlList: urlList,
        setUrlList: setUrlList,
        cursor: cursor,
        setCursor: setCursor,
        // selectedIndex: selectedIndex,
        // setSelectedIndex: setSelectedIndex,
        correctionsFilter: correctionsFilter,
        urlLimit: urlLimit,
        setUrlLimit: setUrlLimit,
        totalListCount: totalListCount,
        setTotalListCount: setTotalListCount,
        endOfPages: endOfPages,
        setEndOfPages: setEndOfPages,
        urlIndex: urlIndex,
        setUrlIndex: setUrlIndex,
      }}
    >
      {shouldDisplayHeader && (
      <Header
      precisionCategories={precisionData.precisionCategoriesOptions}
      urlSearch={urlSearch}
      setUrlSearch={setUrlSearch}
      categoryFilter={categoryFilter}
      setCategoryFilter={setCategoryFilter}
      correctionsFilter={correctionsFilter}
      setCorrectionsFilter={setCorrectionsFilter}
      publisherFilter={publisherFilter}
      setPublisherFilter={setPublisherFilter}
      dateRangeFilter={dateRangeFilter}
      setDateRangeFilter={setDateRangeFilter}
      startDateFilter={startDateFilter}
      setStartDateFilter={setStartDateFilter}
      endDateFilter={endDateFilter}
      setEndDateFilter={setEndDateFilter}
    />
      )
}
      {!shouldDisplayHeader && (
        <TitleLink
        showNote={isReviewPage}
        url={url? url: "none"}
        reviewButton={isReviewPage? false: true}
        correctionButton={isCorrectionsPage? false: true}
        predictiveCorrections={isPredictionsPage? false: true}
        title={getPageTitle(location.pathname)}
        showLink={false}
      />
      )}
      {/* <Outlet /> */}
      <HITLBodyContent />
    </hitlFiltersContext.Provider>
  );
};

export const TierFilter = (
  precisionCategories: PrecisionCategoriesOptionsType[],
  tierLimit: number
) => {
  const filteredCategories = precisionCategories.filter(
    (category) => category.parents.length < tierLimit
  );

  const tieredCategories: { label: string; value: string }[] =
    filteredCategories.map((category) => {
      let newLabel = category.label;
      let newValue = category.label;

      category.parents.forEach((parentIndex) => {
        const parentCategory = filteredCategories.find(
          (parentCategory) => parentCategory.id === Number(parentIndex)
        );

        newLabel = parentCategory?.label + " > " + newLabel;
        // Value doesn't have spaces between > to keep consistency
        newValue = parentCategory?.label + ">" + newValue;
      });

      return {
        label: newLabel,
        value: newValue,
      };
    });

  return tieredCategories;
};

const assignUserPublisher = (
  publisher: AccountsAgencyPartnerChoices | undefined
): {label: string, value: PublisherEnum} => {

  if (!publisher) {
    return {
      label: formatTextDisplay(PublisherEnum.NewsUk),
      value: PublisherEnum.NewsUk
    }
  } else {
    return {
      label: formatTextDisplay(publisherMap(publisher)),
      value: publisherMap(publisher)
    }
  }

}

const getPageTitle = (location: string) => {
  if (location.includes('/review')) {
    return "Review URL"
  } else if (location.includes('/predictive')) {
    return "Predictive Correction"
  } else if (location.includes('/past-review')) {
    return "Correction"
  }

}
